<script setup>
import { ref, computed, watch, onMounted, onActivated } from 'vue';
import { useRouter } from 'vue-router';
import {
    Table,
    Input,
    Segmented,
    Button,
    Spin,
    Tag,
    Empty
} from 'ant-design-vue';
import {
    CalendarOutlined
} from '@ant-design/icons-vue';
import { taipeiDay } from '@/utils/taipeiTime';
import { getConsultantClients } from '@/api/consultation-workspace/consultationWorkspaceApi';

defineOptions({ name: 'ConsultationWorkspaceClients' });

const router = useRouter();
const loading = ref(false);
const filter = ref('upcoming');
const search = ref('');
const clients = ref([]);
const pagination = ref({ page: 1, page_size: 20, total: 0 });
const lastLoadedAt = ref(0);
const STALE_MS = 60_000;

const filterLabel = computed(() =>
    filter.value === 'upcoming' ? '待諮詢學員' : '歷史學員'
);

const columns = computed(() => {
    const base = [
        { title: '學員', key: 'name', width: 160 },
        { title: '下次諮詢', key: 'next_consultation', width: 200 },
        { title: '版本動態', key: 'version_context', width: 240 },
        { title: '操作', key: 'action', width: 120, align: 'right' }
    ];
    if (filter.value === 'history') {
        base.splice(3, 0, {
            title: '諮詢次數',
            key: 'count',
            width: 96,
            align: 'center'
        });
    }
    return base;
});

const load = async () => {
    loading.value = true;
    try {
        const res = await getConsultantClients({
            filter: filter.value,
            search: search.value || undefined,
            page: pagination.value.page,
            page_size: pagination.value.page_size
        });
        if (res.status === 1) {
            clients.value = (res.data.data || []).map((row) => ({
                ...row,
                name: row.name || '未命名學員',
                key: row.user_id
            }));
            pagination.value.total = res.data.total || 0;
            lastLoadedAt.value = Date.now();
        }
    } finally {
        loading.value = false;
    }
};

const refreshIfStale = (force = false) => {
    if (!force && lastLoadedAt.value && Date.now() - lastLoadedAt.value < STALE_MS) {
        return;
    }
    load();
};

const onSearch = () => {
    pagination.value.page = 1;
    load();
};

const goClient = (userId, consultationId = null) => {
    const query = consultationId ? { consultation_id: consultationId } : undefined;
    router.push({
        path: `/consultation-workspace/clients/${userId}`,
        query
    });
};

/** 學員總覽（不帶預約） */
const goClientOverview = (userId) => {
    router.push(`/consultation-workspace/clients/${userId}`);
};

const startConsultation = (record) => {
    const consultationId = record.next_consultation?.id;
    if (!consultationId) {
        goClientOverview(record.user_id);
        return;
    }
    goClient(record.user_id, consultationId);
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const d = taipeiDay(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : String(value);
};

watch(filter, () => {
    pagination.value.page = 1;
    load();
});

onMounted(() => {
    refreshIfStale(false);
});

onActivated(() => {
    refreshIfStale(false);
});
</script>

<template>
    <div class="cw-clients">
        <section class="cw-clients-panel admin-panel">
            <div class="cw-clients-toolbar">
                <div class="cw-clients-toolbar__controls">
                    <div class="cw-clients-toolbar__group">
                        <span class="cw-clients-toolbar__label">篩選</span>
                        <Segmented
                            v-model:value="filter"
                            class="cw-clients-segmented"
                            :options="[
                                { label: '待諮詢', value: 'upcoming' },
                                { label: '歷史', value: 'history' }
                            ]"
                        />
                    </div>
                    <Input.Search
                        v-model:value="search"
                        placeholder="搜尋學員姓名"
                        allow-clear
                        class="cw-clients-search"
                        @search="onSearch"
                    />
                </div>
                <div class="cw-clients-toolbar__stats">
                    <span class="cw-stat">{{ filterLabel }}</span>
                    <span class="cw-stat cw-stat--accent">
                        共 <strong>{{ pagination.total }}</strong> 位
                    </span>
                </div>
            </div>

            <Spin :spinning="loading">
                <Table
                    v-if="clients.length"
                    :columns="columns"
                    :data-source="clients"
                    :pagination="{
                        current: pagination.page,
                        pageSize: pagination.page_size,
                        total: pagination.total,
                        showTotal: (total) => `共 ${total} 位學員`,
                        onChange: (p) => {
                            pagination.page = p;
                            load();
                        }
                    }"
                    class="cw-clients-table"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'name'">
                            <button
                                type="button"
                                class="cw-student-link"
                                @click="goClientOverview(record.user_id)"
                            >
                                <span class="cw-student-link__name">{{ record.name }}</span>
                                <span class="cw-student-link__hint">查看總覽</span>
                            </button>
                        </template>

                        <template v-else-if="column.key === 'next_consultation'">
                            <div
                                v-if="record.next_consultation"
                                class="cw-next-slot"
                            >
                                <CalendarOutlined class="cw-next-slot__icon" />
                                <span class="cw-next-slot__time">{{
                                    formatDateTime(record.next_consultation.starts_at)
                                }}</span>
                                <Tag
                                    :color="
                                        record.next_consultation.mode === 'offline'
                                            ? 'purple'
                                            : 'blue'
                                    "
                                    class="cw-next-slot__mode"
                                >
                                    {{
                                        record.next_consultation.mode === 'offline'
                                            ? '實體'
                                            : '線上'
                                    }}
                                </Tag>
                            </div>
                            <span v-else class="cw-muted">—</span>
                        </template>

                        <template v-else-if="column.key === 'version_context'">
                            <div
                                v-if="record.version_context"
                                class="cw-version-signal"
                            >
                                <div class="cw-version-signal__row">
                                    <Tag
                                        v-if="
                                            record.version_context
                                                .has_new_version_since_booking
                                        "
                                        color="orange"
                                        class="cw-version-signal__tag"
                                    >
                                        預約後有更新
                                    </Tag>
                                    <Tag
                                        v-else
                                        class="cw-version-signal__tag"
                                    >
                                        申請版未變
                                    </Tag>
                                    <span class="cw-version-signal__count">
                                        共 {{ record.version_context.version_count }} 版
                                    </span>
                                </div>
                                <div class="cw-version-signal__meta">
                                    申請版
                                    {{
                                        record.version_context.booking_version?.name ||
                                        '—'
                                    }}
                                    <template
                                        v-if="
                                            record.version_context
                                                .has_new_version_since_booking &&
                                            record.version_context.latest_version?.name
                                        "
                                    >
                                        <span class="cw-version-signal__dot">·</span>
                                        最新
                                        {{
                                            record.version_context.latest_version.name
                                        }}
                                    </template>
                                </div>
                            </div>
                            <span v-else class="cw-muted">進入場次後再選版</span>
                        </template>

                        <template v-else-if="column.key === 'count'">
                            <span class="cw-count-pill">{{
                                record.consultation_count ?? 0
                            }}</span>
                        </template>

                        <template v-else-if="column.key === 'action'">
                            <Button
                                v-if="
                                    record.next_consultation?.id &&
                                    record.next_consultation?.status === 'confirmed'
                                "
                                type="primary"
                                size="small"
                                class="admin-table-actions__btn"
                                @click="startConsultation(record)"
                            >
                                進入場次
                            </Button>
                            <Button
                                v-else
                                type="default"
                                ghost
                                size="small"
                                class="admin-table-actions__btn"
                                @click="goClientOverview(record.user_id)"
                            >
                                查看
                            </Button>
                        </template>
                    </template>
                </Table>

                <Empty
                    v-else-if="!loading"
                    class="cw-clients-empty"
                    :description="
                        search
                            ? '找不到符合的學員，請調整關鍵字'
                            : filter === 'upcoming'
                              ? '目前沒有待諮詢的學員'
                              : '尚無歷史學員紀錄'
                    "
                />
            </Spin>
        </section>
    </div>
</template>

<style scoped>
.cw-clients-panel {
    padding: 20px 22px 24px;
    border-radius: 16px;
    border: 1px solid rgba(196, 163, 90, 0.22);
    background: #fff;
    box-shadow: 0 8px 24px rgba(39, 43, 54, 0.05);
}

.cw-clients-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f2f5;
}

.cw-clients-toolbar__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
}

.cw-clients-toolbar__group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cw-clients-toolbar__label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: #94a3b8;
    text-transform: uppercase;
}

.cw-clients-toolbar__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.cw-stat {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 13px;
    color: #64748b;
}

.cw-stat strong {
    color: #0f172a;
    font-weight: 700;
}

.cw-stat--accent {
    border-color: rgba(139, 105, 20, 0.25);
    background: #f6f1e6;
    color: #8b6914;
}

.cw-stat--accent strong {
    color: #8b6914;
}

.cw-clients-search {
    width: 240px;
}

.cw-clients-segmented :deep(.ant-segmented) {
    background: #f1f5f9;
    padding: 3px;
    border-radius: 10px;
}

.cw-clients-table :deep(.ant-table-thead > tr > th) {
    background: #f6f1e6 !important;
    font-weight: 600;
    color: #5c5346;
}

.cw-student-link {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
}

.cw-student-link:hover .cw-student-link__name {
    color: #8b6914;
}

.cw-student-link__name {
    font-weight: 600;
    color: #272b36;
    transition: color 0.15s;
}

.cw-student-link__hint {
    font-size: 11px;
    color: #a8adb6;
}

.cw-version-signal {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.cw-version-signal__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.cw-version-signal__tag {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
}

.cw-version-signal__count {
    font-size: 12px;
    color: #64748b;
}

.cw-version-signal__meta {
    font-size: 12px;
    color: #94a3b8;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cw-version-signal__dot {
    margin: 0 4px;
}

.cw-next-slot {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.cw-next-slot__icon {
    color: #8b6914;
}

.cw-next-slot__time {
    font-size: 13px;
    font-weight: 500;
    color: #272b36;
}

.cw-next-slot__mode {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
}

.cw-count-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
    color: #8b6914;
    background: #f6f1e6;
    border: 1px solid rgba(139, 105, 20, 0.2);
}

.cw-muted {
    color: #a8adb6;
    font-size: 13px;
}

.cw-clients-empty {
    padding: 48px 0;
}
</style>
